import React, { useCallback, useMemo, useRef, useState } from 'react';
import isNil from 'lodash/isNil';
import { useSelector, useDispatch } from 'react-redux';
import { Cart, Favorite, Search, HeaderMapBalloon } from '@yme/assets/icons';
import { SVGIcon } from '@yme/atoms/svg-icon';
import { CART_URL, CART_URL_PUBLISH } from '@yme/common/constants';
import { getCartCounter, getExternalCartTotalItems, getIsCartConvertedToOrder, } from '@yme/common/redux/cart/selectors';
import { CheckoutSteps } from '@yme/components/CheckoutForm/constants';
import { useWindowSize } from '@yme/hooks/useWindowSize';
import { useWishlist } from '@yme/hooks/useWishlist';
import { setIsModalShowed } from '@yme/modals/actions';
import { MODALS } from '@yme/modals/constants';
import { LanguageSelector } from '@yme/molecules/language-selector';
import { getRTL, getIsInAuthor, getLanguageRootPath, getIsExternalCartEnabled, getExternalCartLink, getExternalCartEndPoint, getRootLang, getEcommerceEnabled, getDealerLocatorPageUrl, getIsKioskVersion, } from '@yme/selectors/common';
import { useTranslation } from '@yme/services/i18n';
import { getFormattedLanguageRootForProductRelease } from '@yme/utils/common';
import { getCustomerId } from '@yme/utils/externalCustomer';
import { SignInMenuItem } from '../SignInMenuItem';
import { SplitterStyled } from '../styled';
import { ShoppingCartPopup } from './ShoppingCartPopup';
import { HIDE_CART_POPUP_TIMEOUT, MAX_LIKES_COUNT, MAX_LIKES_COUNT_LABEL, SHOW_CART_POPUP_TIMEOUT, } from './constants';
import { Badge, CartMenuItem, SearchMenuItem, DealerLocatorMenuItem, ProfileMenuStyled, WishlistMenuItem, CartMenuItemWrapper, } from './styled';
export function ProfileMenu({ countries, labels, onSelectorClickHandler, onSearchClickHandler, isMobile, isSearchEnabled, isCheckoutPage, }) {
    const isRightToLeft = useSelector(getRTL);
    const languageRootPath = useSelector(getLanguageRootPath);
    const isCartConvertedToOrder = useSelector(getIsCartConvertedToOrder);
    const isExternalCartEnabled = useSelector(getIsExternalCartEnabled);
    const externalCartTotalItems = useSelector(getExternalCartTotalItems);
    const externalCartEndPoint = useSelector(getExternalCartEndPoint);
    const externalCartLink = useSelector(getExternalCartLink);
    const rootLanguage = useSelector(getRootLang);
    const isInAuthor = useSelector(getIsInAuthor);
    const ecommerceEnabled = useSelector(getEcommerceEnabled);
    const dealerLocatorPageUrl = useSelector(getDealerLocatorPageUrl);
    const { items, isWishlistEnabled, isAuthorized } = useWishlist();
    const { isLargeDesktopView, isMobileView, isInfiniteDesktopView } = useWindowSize();
    const { t } = useTranslation();
    const [isShowPopup, setIsShowPopup] = useState(false);
    const enterTimeout = useRef(null);
    const leaveTimeout = useRef(null);
    const likesCount = items.length;
    const isShowWishlist = isWishlistEnabled && isAuthorized && Boolean(likesCount) && !isMobileView;
    let cartCounter = useSelector(getCartCounter);
    const isKioskVersion = useSelector(getIsKioskVersion);
    const formattedRootLanguage = getFormattedLanguageRootForProductRelease(rootLanguage);
    const dispatch = useDispatch();
    const showLikes = likesCount > 0;
    const likesLabel = likesCount > MAX_LIKES_COUNT ? MAX_LIKES_COUNT_LABEL : likesCount;
    const likesLink = `${languageRootPath}/wishlist`;
    let cartLink = '';
    const isSoppingCartPopupShowed = useMemo(() => Boolean(!isCheckoutPage && isInfiniteDesktopView && cartCounter), [cartCounter, isCheckoutPage, isInfiniteDesktopView]);
    const handleMouseEnter = useCallback(() => {
        if (leaveTimeout.current) {
            clearTimeout(leaveTimeout.current);
        }
        if (isSoppingCartPopupShowed) {
            enterTimeout.current = setTimeout(() => {
                setIsShowPopup(true);
            }, SHOW_CART_POPUP_TIMEOUT);
        }
    }, [isSoppingCartPopupShowed]);
    const handleMouseLeave = useCallback(() => {
        if (enterTimeout.current) {
            clearTimeout(enterTimeout.current);
        }
        if (isShowPopup) {
            leaveTimeout.current = setTimeout(() => {
                setIsShowPopup(false);
            }, HIDE_CART_POPUP_TIMEOUT);
        }
    }, [isShowPopup]);
    const handleClosePopup = useCallback(() => {
        setIsShowPopup(false);
        if (leaveTimeout.current) {
            clearTimeout(leaveTimeout.current);
        }
    }, []);
    if (isExternalCartEnabled && !isNil(externalCartTotalItems)) {
        const customerId = getCustomerId();
        cartCounter = externalCartTotalItems;
        cartLink = `${externalCartLink}/${formattedRootLanguage}/${externalCartEndPoint}?id=${customerId}`;
    }
    else {
        if (isInAuthor) {
            cartLink = `${languageRootPath}/${CART_URL}`;
        }
        else {
            cartLink = `${languageRootPath}/${CART_URL_PUBLISH}`;
        }
    }
    const handleClickCart = useCallback((event) => {
        if (isExternalCartEnabled) {
            event.preventDefault();
            dispatch(setIsModalShowed({
                type: MODALS.REDIRECTION_EXTERNAL_CART_MODAL,
            }));
        }
        if (isCartConvertedToOrder && window.location.hash !== `#${CheckoutSteps.CART}`) {
            event.preventDefault();
            dispatch(setIsModalShowed({
                type: MODALS.ANOTHER_SESSION_MODAL,
            }));
        }
    }, [dispatch, isCartConvertedToOrder, isExternalCartEnabled]);
    return (React.createElement(ProfileMenuStyled, { isRightToLeft: isRightToLeft },
        !isMobile && isSearchEnabled && (React.createElement(SearchMenuItem, { onClick: onSearchClickHandler, "aria-label": "search" },
            React.createElement(SVGIcon, { type: Search }))),
        !isMobileView && Boolean(dealerLocatorPageUrl) && (React.createElement(DealerLocatorMenuItem, { to: dealerLocatorPageUrl },
            React.createElement(SVGIcon, { type: HeaderMapBalloon }))),
        isShowWishlist && (React.createElement(WishlistMenuItem, { to: likesLink, "aria-label": t('common_wishlistPage_accessibility') },
            React.createElement(SVGIcon, { type: Favorite }),
            showLikes && React.createElement(Badge, { right: -5 }, likesLabel))),
        ecommerceEnabled && (React.createElement(CartMenuItemWrapper, { onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave },
            React.createElement(CartMenuItem, { onClick: handleClickCart, to: cartLink, "aria-label": t('common_cartPage_accessibility') },
                React.createElement(SVGIcon, { type: Cart }),
                cartCounter !== null && cartCounter > 0 && React.createElement(Badge, null, cartCounter)),
            isShowPopup && (React.createElement(ShoppingCartPopup, { handleClosePopup: handleClosePopup, cartLink: cartLink })))),
        !isKioskVersion && React.createElement(SignInMenuItem, { isRightToLeft: isRightToLeft }),
        isLargeDesktopView && React.createElement(SplitterStyled, null),
        !isMobileView && (React.createElement(LanguageSelector, { labels: labels, countries: countries, onSelectorClick: onSelectorClickHandler, isMinified: true }))));
}
ProfileMenu.displayName = 'ProfileMenu';
